import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Stack,
    Chip,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { alpha } from '@mui/material/styles';
import { UserView } from 'core/components/views';
import {
    formatSkillLabelForDisplay,
    getProfileAvatarUrl,
    getProfileDisplayName,
    getProfileAvatarLetter,
    looksLikeEmailAddress,
    nameToAvatarColor,
} from '../utils/profileDisplay';

function CandidatePeopleCard({
    candidate,
    onCardClick,
    onPrimaryClick,
    primaryLabel = 'View profile',
}) {
    const profile = candidate?.profile;
    const name = getProfileDisplayName(profile);
    const avatarUrl = getProfileAvatarUrl(profile);
    const rawTitle = profile?.title?.trim();
    const titleHeadline = rawTitle && !looksLikeEmailAddress(rawTitle) ? rawTitle : '';
    const skillsHeadline = candidate.skills?.length
        ? candidate.skills
              .slice(0, 2)
              .map((s) => formatSkillLabelForDisplay(s))
              .filter(Boolean)
              .join(' · ')
        : '';
    const headline = titleHeadline || skillsHeadline;

    const avatarColor = nameToAvatarColor(name);
    const labeledSkills = (candidate.skills || [])
        .map((sk) => ({ skill: sk, label: formatSkillLabelForDisplay(sk) }))
        .filter((x) => x.label);
    const shownSkills = labeledSkills.slice(0, 3);
    const moreSkills = Math.max(0, labeledSkills.length - 3);

    return (
        <Card
            elevation={0}
            onClick={onCardClick}
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                borderRadius: '14px',
                boxShadow: 'none',
                '&:hover': { borderColor: '#cbd5e1', boxShadow: '0 6px 18px -10px rgba(15,23,42,.25)' },
                '&:hover .cand-link svg': { transform: 'translateX(3px)' },
            }}
        >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Stack direction="row" spacing={1.75} alignItems="center">
                    <Avatar
                        alt={name}
                        src={avatarUrl || undefined}
                        sx={{
                            width: 52,
                            height: 52,
                            flexShrink: 0,
                            fontSize: '1.15rem',
                            fontWeight: 700,
                            bgcolor: alpha(avatarColor, 0.14),
                            color: avatarColor,
                        }}
                    >
                        {getProfileAvatarLetter(profile)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ lineHeight: 1.3 }}>
                            {name}
                        </Typography>
                        {profile?.city && (
                            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ color: 'text.secondary', mt: 0.25 }}>
                                <LocationOnOutlinedIcon sx={{ fontSize: 15 }} />
                                <Typography variant="caption" noWrap>{profile.city}</Typography>
                            </Stack>
                        )}
                    </Box>
                </Stack>

                {headline && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 1.75,
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {headline}
                    </Typography>
                )}

                <UserView>
                    {shownSkills.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.5 }}>
                            {shownSkills.map(({ skill, label }) => (
                                <Chip
                                    key={skill.id}
                                    label={label}
                                    size="small"
                                    sx={{ height: 22, fontSize: '0.72rem', fontWeight: 500, bgcolor: '#f1f5f9', color: 'text.secondary', border: 0 }}
                                />
                            ))}
                            {moreSkills > 0 && (
                                <Chip label={`+${moreSkills}`} size="small" sx={{ height: 22, fontSize: '0.72rem', bgcolor: 'transparent', color: 'text.disabled', border: 0 }} />
                            )}
                        </Stack>
                    )}
                </UserView>

                <Box
                    className="cand-link"
                    onClick={(e) => {
                        e.stopPropagation();
                        onPrimaryClick(e);
                    }}
                    sx={{
                        mt: 'auto',
                        pt: 2,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.85rem',
                        fontWeight: 650,
                        color: 'primary.main',
                        '& svg': { transition: 'transform .15s ease' },
                    }}
                >
                    {primaryLabel}
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Box>
            </CardContent>
        </Card>
    );
}

export default CandidatePeopleCard;
